import { useToggle } from 'ahooks'
import { DatePicker, Form, FormInstance, Modal, Select } from 'antd'
import { useAtom } from 'jotai'
import { isNumber, toNumber } from 'lodash'
import { Moment } from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, UsersSearch } from '@/components'
import { DepartmentTreeTransfer, isIdPureNumber } from '@/components/department-tree-transfer.component'
import { onDepartmentUserCheck } from '@/components/department-tree-with-users.component'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { departmentsTreeAtom } from '@/modules/department'
import { DepartmentUser } from '@/modules/project/project-create/project-form.component'
import { ProjectNameInput } from '@/modules/project/project-create/project-name-input.component'
import { userInfoAtom, usersAtom, usersMapAtom } from '@/modules/user'
import { PriorityEnum, ProjectClassification } from '@/schemas'
import { removeHtmlTag } from '@/utils/remove-html-tag'

export interface PortfolioValues {
  name: string
  email: string
  departmentId: number | null
  date: [Moment, Moment] | null
}

interface ProjectFormProps {
  selfId?: string
  form: FormInstance<PortfolioValues>
  onSubmit: (data: PortfolioValues) => void
  readPermissions?: string[]
}

export const classificationOptionsRaw = Object.entries(ProjectClassification)
  .filter(([value]) => isNumber(Number(value)) && !isNaN(Number(value)))
  .map(([value, key]) => ({
    value: Number(value),
    label: key,
  }))

// 调整展示顺序
export const classificationOptions = classificationOptionsRaw.slice(1).concat(classificationOptionsRaw.slice(0, 1))

export const PortfolioForm: React.FC<ProjectFormProps> = ({
  selfId,
  form,
  onSubmit,
  readPermissions = [],
  children,
}) => {
  const { t } = useTranslation()
  const [userInfo] = useAtom(userInfoAtom)
  const [users] = useAtom(usersAtom)
  const [departments] = useAtom(departmentsTreeAtom)
  const [usersMap] = useAtom(usersMapAtom)
  const [modalVisible, { toggle: modalToggle }] = useToggle()
  const [departmentUsers, setDepartmentUsers] = React.useState<DepartmentUser[]>([])

  const onFinish = () => {
    form
      .validateFields()
      .then((data) => {
        onSubmit({
          ...data,
          departmentId: data.departmentId || null,
          startDate: data.date && data.date[0].format('YYYY-MM-DD'),
          endDate: data.date && data.date[1].format('YYYY-MM-DD'),
        })
      })
      .catch((err) => {
        console.error(err)
      })
  }

  const onValuesChange = (changed: Partial<PortfolioValues>) => {
    const email = changed.email
    if (email && email in usersMap) {
      form.setFields([{ name: 'departmentId', value: usersMap[email].departmentIds[0] }])
    }
  }

  React.useEffect(() => {
    const defaultCheckedKeys =
      readPermissions.length > 0
        ? readPermissions
            .map((permission) => {
              // departmentId
              if (isIdPureNumber(permission)) {
                return Number(permission)
              }
              // userId
              const user = users.find((user) => user.email === permission)
              return user ? `${user.departmentIds[0]}-${user.email}` : null
            })
            .filter(Boolean)
            .map((id) => id!)
        : []
    onDepartmentUserCheck(defaultCheckedKeys, departments, users, setDepartmentUsers)
  }, [])

  return (
    <div className='flex flex-wrap'>
      <div className='w-500px'>
        <Form
          name='basic'
          layout='horizontal'
          labelCol={{ span: 24 }}
          form={form}
          onValuesChange={onValuesChange}
          onFinish={onFinish}
        >
          <ProjectNameInput selfId={selfId} type='portfolio' />

          <Form.Item label={t('portfolio.owner')} name='email' rules={[{ required: true }]}>
            <UsersSearch placeholder={t('search users')} />
          </Form.Item>

          <Form.Item label={t('department')} name='departmentId' className='hidden'>
            {/* <DepartmentSearch /> */}
          </Form.Item>

          <Form.Item label={t('portfolio.cycle')} name='date'>
            <DatePicker.RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('portfolio.classification')} required name='classification' rules={[{ required: true }]}>
            <Select placeholder={t('portfolio.classification')} options={classificationOptions}></Select>
          </Form.Item>

          <Form.Item
            label={t('resource.background')}
            tooltip={t('devlop.form.background.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='background'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.background')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>

          <Form.Item
            label={t('resource.goalDesc')}
            tooltip={t('devlop.form.goal.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='goal'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.goalDesc')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>
          <Form.Item
            label={t('resource.checkStandard')}
            tooltip={t('devlop.form.standard.tooltip')}
            valuePropName='content'
            trigger='onChange'
            name='standard'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.checkStandard')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>
          <Form.Item
            label={t('resource.challenge')}
            tooltip={t('resource.challengeTooltip')}
            valuePropName='content'
            trigger='onChange'
            name='challenge'
            rules={[
              {
                required: true,
                validator: async (_, value) =>
                  !removeHtmlTag(value).trim()
                    ? Promise.reject(`${t('resource.challenge')}是必选填项!`)
                    : Promise.resolve(),
              },
            ]}
          >
            <Editor disableAutoSaveStyle className={'editorBorder'} />
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            label={
              <span>
                {t('devlop.detail.priority')}
                <PriorityPopoverDescription />
              </span>
            }
            name='priority'
          >
            <Select
              options={Object.keys(PriorityEnum)
                .filter((v) => isNaN(toNumber(v)))
                .map((key) => ({
                  label: t(`form.priority.${key}`),
                  value: PriorityEnum[key],
                }))}
            />
          </Form.Item>
          <Form.Item wrapperCol={{ span: 8, offset: 0 }} className='mt-32px'>
            {children}
          </Form.Item>
        </Form>
      </div>
      <Modal
        width={700}
        visible={modalVisible}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => modalToggle()}
        destroyOnClose
      >
        <DepartmentTreeTransfer
          users={users}
          departments={departments}
          userInfo={userInfo}
          defaultPermissions={readPermissions}
          departmentUsers={departmentUsers}
          setDepartmentUsers={setDepartmentUsers}
        />
      </Modal>
    </div>
  )
}
